// src/thirdPartyLibraries/three-mesh-halfedge/src/operations/createQuadFace.ts
import { HalfedgeDS } from "../core/HalfedgeDS";
import { Vertex } from "../core/Vertex";
import { createFace } from "./createFace";


/**
 * Robustly stitches a quad (v1, n1, n2, v2) by trying a few safe triangulations.
 * Returns true if we managed to add at least one triangle (ideally two).
 *
 * Notes:
 * - We do NOT assert boundary state here (outer loop vs old loop). That evolves
 *   as neighboring quads are added; checking it here causes flaky behavior.
 * - We rely on addFaceByVertices, which ensures a FREE directed halfedge exists,
 *   creating a parallel edge if needed (allowParallels=true fallback inside).
 */
export function createQuadFace(
  ds: HalfedgeDS,
  v1: Vertex, n1: Vertex, n2: Vertex, v2: Vertex,
): boolean {
  const tri = (a: Vertex, b: Vertex, c: Vertex) => {
    if (a === b || b === c || c === a) return false;
    // Degeneracy filter via triple product area2:
    const abx = b.position.x - a.position.x;
    const aby = b.position.y - a.position.y;
    const abz = b.position.z - a.position.z;
    const acx = c.position.x - a.position.x;
    const acy = c.position.y - a.position.y;
    const acz = c.position.z - a.position.z;
    const area2 = Math.hypot(
      aby * acz - abz * acy,
      abz * acx - abx * acz,
      abx * acy - aby * acx,
    );
    if (area2 <= 1e-12) return false;
    return createFace(ds, a, b, c) === true;
  };

  // Preferred split: (v1, n1, n2) + (v1, n2, v2)
  let added = 0;
  if (tri(v1, n1, n2)) added++;
  if (tri(v1, n2, v2)) added++;

  // If second failed (often because v1→v2 direction is occupied),
  // try alternative windings that use the FREE boundary halfedge side.
  if (added < 2) {
    added += Number(tri(v1, v2, n2));
    added += Number(tri(v2, n2, n1));
    added += Number(tri(n2, v2, v1));
    added += Number(tri(v1, n1, v2));
  }

  return added > 0;
}